import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowDown, CalendarCheck, Quote, Heart, Sparkles, Megaphone,
  Star, Database, GraduationCap, MessageSquare, Phone, Mail,
  FileSpreadsheet, BarChart3, Wrench, Lock, Compass, Zap, Shield, Layers,
} from "lucide-react";
import { useDemo } from "@/components/landing/DemoContext";
import { FinalCTA } from "@/components/landing/FinalCTA";
import { DiscChapters } from "@/components/welcome/DiscChapters";
import { WHY_CHAPTERS } from "@/data/discChapters";

/* /why-clinicfirst — Editorial story page.
   Distinct from the rest of the site: dark/light alternating chapters,
   oversized chapter numbers, two-voice narrative, narrow editorial typography. */

const TOOLS_CHAOS = [
  { name: "SharpSpring",     job: "Marketing automation",          Icon: Megaphone,        rot: -3 },
  { name: "GradeUS",         job: "Review collection",             Icon: Star,             rot:  2 },
  { name: "Juvonno",         job: "EMR · clinical records",        Icon: Database,         rot: -1 },
  { name: "Google Classroom",job: "Staff training & onboarding",   Icon: GraduationCap,    rot:  3 },
  { name: "Google Chat",     job: "Internal communication",        Icon: MessageSquare,    rot: -2 },
  { name: "Call Hero",       job: "Inbound + outbound calls",      Icon: Phone,            rot:  1 },
  { name: "Gmail",           job: "Patient + ops email",           Icon: Mail,             rot: -3 },
  { name: "Excel sheets",    job: "Clinician + clinic KPIs",       Icon: FileSpreadsheet,  rot:  2 },
  { name: "Reporting tool",  job: "Slot utilisation · manual",     Icon: BarChart3,        rot: -1 },
  { name: "3 more apps",     job: "Manual workarounds",            Icon: Wrench,           rot:  3 },
];

const LEAKS = [
  "Lead → website → phone → booking — every step in a different tool. Patients fell through every seam.",
  "Clinician utilisation, retention, no-shows lived in Excel. Numbers were always 2 weeks stale.",
  "Reception counted open slots by eye every morning. Owners learned utilisation a week later.",
  "No-show follow-ups lived in someone's head. Most callbacks never happened.",
  "Marketing drove leads. Leads disappeared into 7 silos. No one could attribute revenue to a channel.",
  "Top performers got noticed; the silent middle did not. No live signal of who needed coaching.",
];

const TIMELINE = [
  { tag: "Chapter 0", month: "M 00", title: "Two founders. One industry problem.",
    body: "Two separate businesses — Nevin running marketing & SaaS across clinic verticals, Mathew running OPTSC. Same problem from opposite angles. They meet." },
  { tag: "Catalyst",  month: "M 02", title: "The conversation that kept circling.",
    body: "Every conversation kept landing on the same gap: clinics had marketing, EMRs and spreadsheets — but nothing tied them together. The decision: co-found an operating system for clinics." },
  { tag: "V0",        month: "M 03", title: "Unified Inbox prototype ships.",
    body: "First module: pull calls, SMS, email and form submissions into one place. Reception loves it. Rebuild #1 begins the next week." },
  { tag: "V1",        month: "M 04 — 09", title: "Foundations — Hub · Call IQ · Pulse.",
    body: "Core platform takes shape. Every module rebuilt four times. Mathew's veto on &lsquo;this won&apos;t work on a Tuesday morning&rsquo; kills three layouts." },
  { tag: "V2",        month: "M 09 — 14", title: "KAI is born.",
    body: "Owners ask the same seven questions every week. KAI is built to answer them — and to draft replies, classify leads, surface alerts. It becomes the layer everything plugs into." },
  { tag: "V3",        month: "M 14 — 17", title: "Pulse 2.0 — built for franchises.",
    body: "OPTSC scales operations across all 10 locations. The dashboard built for one clinic doesn&apos;t work for ten. Executive Reporting Dashboard ships." },
  { tag: "V4",        month: "M 17 — 18", title: "AI Receptionist + Voice Agents.",
    body: "After 3 rings, KAI picks up. Books, reschedules, cancels — writes back to Juvonno in under 2 seconds. Hub+, Call IQ AI and Pulse get one last refinement pass. Total: 8 modules · 28 platform features shipped." },
  { tag: "Today",     month: "M 18+", title: "Clinic OS opens to other clinics.",
    body: "After 18 months running inside OPTSC's 10 locations, Clinic OS opens to other clinic owners. 8 modules. Native EMR. Live in under 14 days." },
];

const PRINCIPLES = [
  { Icon: Database, title: "EMR · source of truth",  body: "Your EMR stays the chart. Clinic OS reads it — never edits, never deletes." },
  { Icon: Sparkles, title: "Ease of use",                body: "If reception can&apos;t run it on day one, we redesign it." },
  { Icon: Shield,   title: "Privacy & governance",       body: "HIPAA-ready, SOC 2 aligned. Your data, your rules — always." },
  { Icon: Zap,      title: "AI companion",               body: "KAI sits beside the team. Picks up calls, drafts replies, surfaces signals." },
  { Icon: Layers,   title: "Operations, unified",        body: "Shared signals across every team — data that lifts the whole clinic, not just one role." },
];

const TODAY = [
  { stat: "8",     label: "modules in the OS",     foot: "Across eight categories" },
  { stat: "28",    label: "features shipped",      foot: "Refined over 18 months" },
  { stat: "18 mo", label: "to open beyond OPTSC",  foot: "Forged inside 10 locations" },
  { stat: "100%",  label: "informed by real ops",  foot: "No vapor · no fake stats" },
];

/* ─── Reusable editorial chapter wrapper ─────────────────────────────── */
const Chapter = ({ n, kicker, dark = false, accent = "violet", children }) => {
  const accentMap = {
    violet:  { from: "#7c3aed", to: "#d946ef" },
    rose:    { from: "#f43f5e", to: "#ec4899" },
    sky:     { from: "#0ea5e9", to: "#14b8a6" },
    amber:   { from: "#f59e0b", to: "#f97316" },
    emerald: { from: "#10b981", to: "#14b8a6" },
  };
  const a = accentMap[accent] || accentMap.violet;
  return (
    <section
      data-testid={`why-chapter-${n}`}
      className={`relative overflow-hidden ${dark ? "bg-[#0a0a14] text-white" : "bg-background text-foreground"}`}
    >
      {/* Oversized outline chapter number */}
      <span
        aria-hidden
        className="pointer-events-none absolute -top-6 right-2 select-none font-display text-[140px] font-black tracking-tighter sm:-top-8 sm:right-6 sm:text-[200px] lg:text-[280px]"
        style={{
          color: "transparent",
          WebkitTextStroke: dark ? "1px rgba(255,255,255,0.06)" : "1px rgba(0,0,0,0.06)",
        }}
      >
        {n}
      </span>

      <div className="relative mx-auto max-w-5xl px-6 py-20 sm:px-10 sm:py-28 lg:px-16">
        <p
          className="text-[10.5px] font-bold uppercase tracking-[0.32em]"
          style={{ color: dark ? a.to : a.from }}
        >
          {kicker}
        </p>
        {children}
      </div>
    </section>
  );
};

/* Editorial drop-cap paragraph wrapper · standardised reading size */
const Lede = ({ children }) => (
  <p className="font-display text-[18px] leading-[1.65] text-foreground/85 first-letter:float-left first-letter:mr-2 first-letter:font-display first-letter:text-[60px] first-letter:font-black first-letter:leading-[0.85] dark:text-white/85 sm:text-[19px]">
    {children}
  </p>
);

export default function WhyClinicFirst() {
  const openDemo = useDemo();

  return (
    <article data-testid="why-clinicfirst" className="overflow-hidden">

      {/* ══════════════════════════════════════════════════════════════════════
         COVER · the magazine cover page
         ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden bg-[#0a0a14] text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-50"
          style={{ background: "radial-gradient(55% 70% at 70% 0%, rgba(217,70,239,0.25), transparent 70%), radial-gradient(45% 55% at 20% 80%, rgba(124,58,237,0.20), transparent 70%)" }}
        />
        <div className="relative mx-auto max-w-5xl px-5 pt-16 pb-12 sm:px-10 sm:pt-40 sm:pb-32 lg:px-16">
          <p className="text-[10px] sm:text-[10.5px] font-bold uppercase tracking-[0.28em] sm:tracking-[0.4em] text-white/60">
            A founders&apos; story · Issue 01
          </p>
          <div className="mt-3 h-px w-16 bg-white/30" />

          <h1 className="mt-5 sm:mt-10 font-display text-[2.05rem] font-black leading-[0.98] tracking-tight sm:text-6xl lg:text-[4.5rem]">
            Two founders.<br />
            Two industries.<br />
            <span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-rose-400 bg-clip-text italic text-transparent">One clinic problem.</span>
          </h1>

          <p className="mt-4 sm:mt-8 max-w-2xl text-[14px] leading-relaxed text-white/70 sm:text-base">
            How a marketing &amp; SaaS founder and a 10-location physiotherapy franchise owner collided around the same gap in the clinic industry — and spent 18 months building the operating system the industry had been duct-taping together.
          </p>

          <div className="mt-7 hidden items-center gap-4 sm:mt-12 sm:flex">
            <div className="h-px flex-1 bg-white/15" />
            <ArrowDown className="h-4 w-4 text-white/40" />
            <span className="text-[10.5px] font-bold uppercase tracking-[0.3em] text-white/40">Begin reading</span>
            <div className="h-px flex-1 bg-white/15" />
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
         CHAPTER 01 · MATHEW — INSIDE THE CLINIC
         ══════════════════════════════════════════════════════════════════════ */}
      <Chapter n="01" kicker="Chapter one · inside the clinic" dark accent="rose">
        <h2 className="mt-6 font-display text-3xl font-black leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
          Mathew was duct-taping{" "}
          <span className="bg-gradient-to-r from-rose-400 to-pink-400 bg-clip-text italic text-transparent">ten locations</span>{" "}
          together.
        </h2>

        <div className="mt-10 grid gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-7">
            <p className="font-display text-[19px] leading-relaxed text-white/85 first-letter:float-left first-letter:mr-2 first-letter:font-display first-letter:text-[64px] first-letter:font-black first-letter:leading-[0.85] sm:text-xl">
              Mathew Puickal was running Ottawa Physiotherapy and Sports Clinic across ten locations. By every external measure, the business was winning — bookings full, clinicians great, patients loyal. By every internal measure, he was bleeding hours every week reconciling spreadsheets.
            </p>
            <p className="mt-6 text-[15px] leading-relaxed text-white/70 sm:text-base">
              Every Monday morning meant a stack of seven different tools that didn&apos;t talk to each other. Marketing data lived in <em className="not-italic text-white/90">SharpSpring</em>. Patient charts in <em className="not-italic text-white/90">Juvonno</em>. Reviews in <em className="not-italic text-white/90">GradeUS</em>. Calls in <em className="not-italic text-white/90">Call Hero</em>. Performance KPIs in <em className="not-italic text-white/90">Excel</em>. Communications across <em className="not-italic text-white/90">Gmail</em>, <em className="not-italic text-white/90">Google Chat</em> and a few more. Reception spent mornings reconciling instead of booking.
            </p>
            <p className="mt-6 text-[15px] leading-relaxed text-white/70 sm:text-base">
              No single source of truth. No real-time KPI. Patients fell through the seams between tools — and so did revenue.
            </p>
          </div>

          {/* Mathew profile card */}
          <div className="lg:col-span-5">
            <div className="relative overflow-hidden rounded-3xl border border-white/15 bg-white/[0.04] p-6 backdrop-blur-sm sm:p-7">
              <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-gradient-to-br from-sky-400 to-emerald-400 opacity-25 blur-3xl" />
              <div className="relative flex items-center gap-5">
                <span className="inline-flex h-16 w-16 flex-none items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-emerald-500 font-display text-xl font-black text-white shadow-md ring-1 ring-inset ring-white/15">
                  MP
                </span>
                <div>
                  <p className="font-display text-2xl font-black tracking-tight">Mathew Puickal</p>
                  <p className="mt-0.5 text-[12px] font-bold uppercase tracking-[0.18em] text-sky-300">
                    Co-founder · OPTSC
                  </p>
                </div>
              </div>
              <p className="relative mt-5 text-[13.5px] leading-relaxed text-white/80">
                Physiotherapist by training. Built Ottawa Physiotherapy and Sports Clinic into 10 locations. The voice in every product review who could say <em className="not-italic text-white">&ldquo;this won&apos;t work on a Tuesday morning&rdquo;</em> — and the reason every module shipped four times before it was right.
              </p>
            </div>
          </div>
        </div>

        {/* The tool chaos — scattered cards */}
        <p className="mt-16 text-[10.5px] font-bold uppercase tracking-[0.3em] text-white/40">
          The duct tape · ten tools that didn&apos;t speak to each other
        </p>
        <div className="mt-6 grid gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
          {TOOLS_CHAOS.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 8, rotate: 0 }}
              whileInView={{ opacity: 1, y: 0, rotate: t.rot }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.35, delay: (i % 5) * 0.04 }}
              className="rounded-xl border border-rose-400/30 bg-rose-500/[0.08] p-3.5 transition-transform hover:rotate-0"
            >
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-rose-500/20 text-rose-300">
                <t.Icon className="h-4 w-4" />
              </span>
              <p className="mt-2.5 font-display text-[13px] font-black tracking-tight">{t.name}</p>
              <p className="mt-0.5 text-[10.5px] leading-snug text-white/55">{t.job}</p>
            </motion.div>
          ))}
        </div>
      </Chapter>

      {/* ══════════════════════════════════════════════════════════════════════
         CHAPTER 02 · NEVIN — ACROSS THE INDUSTRY
         ══════════════════════════════════════════════════════════════════════ */}
      <Chapter n="02" kicker="Chapter two · across the industry" accent="violet">
        <h2 className="mt-6 font-display text-3xl font-black leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
          Nevin kept seeing the{" "}
          <span className="bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text italic text-transparent">same story</span>{" "}
          at every clinic.
        </h2>

        <div className="mt-10 grid gap-10 lg:grid-cols-12 lg:gap-14">
          {/* Nevin profile card */}
          <div className="lg:col-span-5 lg:order-1">
            <div className="relative overflow-hidden rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-7">
              <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 opacity-15 blur-3xl" />
              <div className="relative flex items-center gap-5">
                <span className="inline-flex h-16 w-16 flex-none items-center justify-center rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 font-display text-xl font-black text-white shadow-md ring-1 ring-inset ring-white/15">
                  NP
                </span>
                <div>
                  <p className="font-display text-2xl font-black tracking-tight">Nevin Porwal</p>
                  <p className="mt-0.5 text-[12px] font-bold uppercase tracking-[0.18em] text-violet-600 dark:text-violet-300">
                    Co-founder · Marketing &amp; SaaS
                  </p>
                </div>
              </div>
              <p className="relative mt-5 text-[13.5px] leading-relaxed text-foreground/85">
                Built and ran a marketing &amp; SaaS company across clinic verticals. The one who looked at twenty different clinics and saw the same operational mess — and realised the industry didn&apos;t have a tooling problem. It had a <em className="not-italic font-semibold">stitching</em> problem.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7 lg:order-2">
            <Lede>
              From the marketing seat, Nevin worked with clinics across physiotherapy, dental, spa and chiropractic. Different specialities, different cities, different stack — but the same broken pattern every time. Campaigns drove leads. Leads disappeared into seven silos. Owners couldn&apos;t prove ROI. Reception was always behind.
            </Lede>
            <p className="mt-6 text-[15px] leading-relaxed text-foreground/75 sm:text-base">
              It wasn&apos;t one clinic&apos;s problem. It was the <em className="not-italic font-semibold">industry&apos;s</em> problem. No SaaS on the market did this end-to-end. Every clinic was rebuilding the same duct-taped stack — and quietly losing revenue between the seams.
            </p>

            {/* Mini industries strip */}
            <div className="mt-7 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
              {["Physiotherapy", "Dental", "Med Spa", "Chiropractic"].map((sp, i) => (
                <div key={sp} className="rounded-xl border border-violet-500/25 bg-violet-500/[0.06] px-3 py-2.5">
                  <p className="text-[9.5px] font-bold uppercase tracking-[0.16em] text-violet-600 dark:text-violet-300">
                    Vertical #{i + 1}
                  </p>
                  <p className="mt-1 font-display text-[13.5px] font-black tracking-tight">{sp}</p>
                  <p className="mt-0.5 text-[10.5px] text-muted-foreground">Same chaos.</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Industry punch line — universalize the pain */}
        <div className="mx-auto mt-14 max-w-3xl border-l-4 border-violet-500/60 pl-6 sm:pl-8">
          <p className="font-display text-2xl font-black leading-[1.15] tracking-tight text-foreground sm:text-[28px]">
            You&apos;re not understaffed.{" "}
            <span className="bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text italic text-transparent">
              You&apos;re under-stitched.
            </span>
          </p>
          <p className="mt-3 text-[14.5px] leading-relaxed text-foreground/70 sm:text-[15px]">
            Every clinic Nevin walked into had great clinicians, great patients and a great brand. What they didn&apos;t have was one thread running from a Google ad to a Juvonno chart to a Tuesday-morning roster. That missing thread cost real revenue — and nobody on the market was selling it.
          </p>
        </div>
      </Chapter>

      {/* ══════════════════════════════════════════════════════════════════════
         CHAPTER 03 · TWO PATHS CONVERGE
         ══════════════════════════════════════════════════════════════════════ */}
      <Chapter n="03" kicker="Chapter three · the collaboration" dark accent="violet">
        <h2 className="mt-6 font-display text-3xl font-black leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
          Two paths{" "}
          <span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-rose-400 bg-clip-text italic text-transparent">converged.</span>
        </h2>

        {/* Two paths visual */}
        <div className="mx-auto mt-12 max-w-3xl">
          <div className="grid grid-cols-2 gap-6">
            <div className="rounded-2xl border border-sky-400/30 bg-sky-500/[0.06] p-5">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-sky-300">Mathew</p>
              <p className="mt-1 font-display text-[15px] font-black tracking-tight text-white">From inside a 10-location clinic</p>
              <p className="mt-1.5 text-[12px] leading-snug text-white/65">Lived the chaos every Monday morning.</p>
            </div>
            <div className="rounded-2xl border border-violet-400/30 bg-violet-500/[0.06] p-5">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-violet-300">Nevin</p>
              <p className="mt-1 font-display text-[15px] font-black tracking-tight text-white">From across the industry</p>
              <p className="mt-1.5 text-[12px] leading-snug text-white/65">Saw the pattern repeat across every vertical.</p>
            </div>
          </div>

          {/* SVG converging arrows */}
          <svg viewBox="0 0 600 80" className="mx-auto mt-2 h-16 w-full max-w-md" fill="none">
            <defs>
              <linearGradient id="convA" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#38bdf8" />
                <stop offset="1" stopColor="#d946ef" />
              </linearGradient>
              <linearGradient id="convB" x1="1" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#a855f7" />
                <stop offset="1" stopColor="#d946ef" />
              </linearGradient>
            </defs>
            <motion.path
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2 }}
              d="M 60 10 Q 200 10 300 70"
              stroke="url(#convA)" strokeWidth="2" strokeLinecap="round" fill="none"
            />
            <motion.path
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2 }}
              d="M 540 10 Q 400 10 300 70"
              stroke="url(#convB)" strokeWidth="2" strokeLinecap="round" fill="none"
            />
          </svg>

          {/* The merged moment */}
          <div className="relative overflow-hidden rounded-2xl border border-white/15 bg-gradient-to-br from-violet-600/15 via-fuchsia-600/12 to-rose-500/12 p-6 text-center backdrop-blur-sm">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-fuchsia-300">The decision</p>
            <p className="mt-2 font-display text-xl font-black leading-tight tracking-tight text-white sm:text-2xl">
              Co-found Clinic OS.<br />Solve the seam — not another point tool.
            </p>
          </div>
        </div>

        <div className="mx-auto mt-12 max-w-3xl">
          <p className="font-display text-[18px] leading-[1.65] text-white/85 sm:text-[19px]">
            Same problem, opposite angles. Mathew lived it every morning across 10 clinics. Nevin watched it repeat at every clinic he advised. So they stopped patching the stack — and started over.
          </p>
          <p className="mt-5 font-display text-[18px] leading-[1.65] text-white/85 sm:text-[19px]">
            Not another CRM. Not another marketing tool. A real <em className="not-italic font-semibold text-white">operating system</em> — one that respects the EMR instead of replacing it, lets reception lead the day instead of chasing it, and was built by people who actually run clinics, not just dashboards.
          </p>
        </div>

        {/* Six leaks list — newspaper-style */}
        <div className="mx-auto mt-14 max-w-3xl">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.3em] text-rose-300">
            Six leaks we found · before writing a line of code
          </p>
          <ul className="mt-6 space-y-3 border-l border-white/15 pl-6">
            {LEAKS.map((l, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -8 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
                className="relative pl-3 text-[14px] leading-relaxed text-white/80"
              >
                <span className="absolute left-[-29px] top-1 font-mono text-[10.5px] font-bold text-white/40">{String(i + 1).padStart(2, "0")}</span>
                {l}
              </motion.li>
            ))}
          </ul>
        </div>
      </Chapter>

      {/* ══════════════════════════════════════════════════════════════════════
         INTERLUDE PULL-QUOTE — subtle, editorial
         ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative bg-[#0a0a14] py-20 text-white sm:py-24">
        <div className="mx-auto max-w-3xl px-6 sm:px-10 lg:px-16">
          <div className="flex items-start gap-6">
            <Quote className="mt-1 h-6 w-6 flex-none text-fuchsia-400/70" />
            <div>
              <blockquote className="font-display text-2xl font-bold leading-snug tracking-tight text-white/95 sm:text-[28px]">
                We built an operating system for clinics — because no CRM ever did the job.
              </blockquote>
              <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.28em] text-white/40">
                Nevin &amp; Mathew · Co-founders, Clinic OS
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
         CHAPTER 04 · 18 MONTHS OF BUILDING
         ══════════════════════════════════════════════════════════════════════ */}
      <Chapter n="04" kicker="Chapter four · eighteen months of building" accent="amber">
        <h2 className="mt-6 font-display text-3xl font-black leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
          Eighteen months. Eight modules.{" "}
          <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text italic text-transparent">Twenty-eight platform features.</span>
        </h2>

        <div className="mx-auto mt-5 max-w-3xl">
          <p className="text-[15px] leading-relaxed text-foreground/75 sm:text-base">
            Every feature shipped against real clinic feedback — not whiteboards. Refined in the field, week after week, across OPTSC&apos;s 10 locations.
          </p>

          <ol className="mt-12 space-y-7 border-l-2 border-amber-500/30 pl-7">
            {TIMELINE.map((t, i) => (
              <motion.li
                key={t.title}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.35, delay: i * 0.04 }}
                className="relative"
                data-testid={`why-timeline-${i}`}
              >
                <span className="absolute -left-[35px] top-1 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-gradient-to-br from-amber-500 to-orange-500 ring-4 ring-background" />
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-amber-700 dark:text-amber-300">
                    {t.tag}
                  </span>
                  <span className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{t.month}</span>
                </div>
                <h3 className="mt-2 font-display text-xl font-black tracking-tight sm:text-2xl">{t.title}</h3>
                <p
                  className="mt-1.5 text-[14px] leading-relaxed text-foreground/75"
                  dangerouslySetInnerHTML={{ __html: t.body }}
                />
              </motion.li>
            ))}
          </ol>
        </div>
      </Chapter>

      {/* ══════════════════════════════════════════════════════════════════════
         CHAPTER 05 · WHAT IT IS TODAY
         ══════════════════════════════════════════════════════════════════════ */}
      <Chapter n="05" kicker="Chapter five · the operating system" dark accent="emerald">
        <h2 className="mt-6 font-display text-3xl font-black leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
          What 18 months{" "}
          <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text italic text-transparent">delivered.</span>
        </h2>

        <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {TODAY.map((t, i) => (
            <motion.div
              key={t.label}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.35, delay: i * 0.06 }}
              className="group relative overflow-hidden rounded-2xl border border-white/15 bg-white/[0.04] p-5 backdrop-blur-sm"
            >
              <div className="pointer-events-none absolute -right-12 -top-12 h-28 w-28 rounded-full bg-gradient-to-br from-emerald-400 to-teal-400 opacity-15 blur-3xl transition-opacity group-hover:opacity-30" />
              <p className="relative bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text font-display text-3xl font-black tracking-tight text-transparent sm:text-4xl">{t.stat}</p>
              <p className="relative mt-1 text-[13px] font-semibold leading-tight text-white">{t.label}</p>
              <p className="relative mt-1 text-[11px] leading-snug text-white/60">{t.foot}</p>
            </motion.div>
          ))}
        </div>

        {/* Principles — column rule layout */}
        <div className="mt-16">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.3em] text-emerald-300">
            Five principles · never changed
          </p>
          <div className="mt-6 grid gap-x-6 gap-y-8 border-t border-white/15 pt-8 sm:grid-cols-2 lg:grid-cols-5">
            {PRINCIPLES.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-300">
                  <p.Icon className="h-4 w-4" />
                </span>
                <p className="mt-3.5 font-display text-[13.5px] font-black leading-tight tracking-tight text-white">{p.title}</p>
                <p
                  className="mt-1.5 min-h-[4.5em] text-[12px] leading-relaxed text-white/65"
                  dangerouslySetInnerHTML={{ __html: p.body }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </Chapter>

      {/* ══════════════════════════════════════════════════════════════════════
         CHAPTER 06 · TRIBUTE TO OPTSC — extended, graceful
         ══════════════════════════════════════════════════════════════════════ */}
      <Chapter n="06" kicker="Chapter six · with gratitude" accent="emerald">
        <h2 className="mt-6 font-display text-3xl font-black leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
          To <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text italic text-transparent">OPTSC</span> — for letting us build this with you.
        </h2>

        <div className="mx-auto mt-10 max-w-3xl">
          <Lede>
            Eighteen months of patience. Twenty-eight platform features. Ten locations volunteering as the testing ground. Reception teams calling us at 9pm to say &ldquo;hey, this would be way better if&hellip;&rdquo; — and us shipping the fix by Monday.
          </Lede>
          <p className="mt-6 text-[15px] leading-[1.7] text-foreground/75">
            Clinic OS exists because OPTSC believed it could. Every product decision is still informed by what works at 8 AM on a Tuesday inside one of their locations — and what we&apos;d break if we tried to ship before it was ready.
          </p>
          <p className="mt-4 text-[15px] leading-[1.7] text-foreground/75">
            They&apos;re a 10-location physiotherapy and sports clinic group serving the Ottawa region — one of the largest and most operationally mature physio networks in the country. Their owners, clinicians and reception teams are the reason every screen in Clinic OS earns its place. They didn&apos;t just give us feedback; they gave us their floor, their phones, their patients&apos; trust, and their honest opinion when something wasn&apos;t good enough yet.
          </p>
          <p className="mt-4 text-[15px] leading-[1.7] text-foreground/75">
            We owe them more than a thank-you. We owe them an operating system built the way they&apos;d build it themselves — and that&apos;s what we keep trying to ship, every week.
          </p>

          {/* OPTSC partner card — graceful with link */}
          <div className="mt-10 overflow-hidden rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.07] via-teal-500/[0.05] to-sky-500/[0.05]">
            <div className="flex flex-col gap-5 p-6 sm:flex-row sm:items-center sm:gap-6 sm:p-7">
              <span className="inline-flex h-14 w-14 flex-none items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-md ring-1 ring-inset ring-white/15">
                <Heart className="h-6 w-6" />
              </span>
              <div className="flex-1">
                <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-emerald-700 dark:text-emerald-300">
                  Founding partner clinic
                </p>
                <p className="mt-1 font-display text-xl font-black tracking-tight sm:text-2xl">
                  Ottawa Physiotherapy &amp; Sports Clinic
                </p>
                <p className="mt-1.5 text-[13.5px] leading-snug text-foreground/75">
                  10 locations across the Ottawa region · physiotherapy · sports rehabilitation · the original clinic OS testing ground.
                </p>
              </div>
              <a
                href="https://www.opsc.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="why-optsc-visit"
                className="inline-flex h-11 flex-none items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 px-5 text-[13px] font-bold text-white shadow-md ring-1 ring-inset ring-white/15 transition-all hover:-translate-y-0.5 hover:shadow-lg"
              >
                Visit www.optsc.com
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </div>

            {/* OPTSC at-a-glance grid */}
            <div className="grid grid-cols-2 divide-x divide-y divide-emerald-500/20 border-t border-emerald-500/20 sm:grid-cols-4 sm:divide-y-0">
              {[
                { k: "Locations", v: "10" },
                { k: "Region",    v: "Ottawa, ON" },
                { k: "Specialty", v: "Physio · Sports Rehab" },
                { k: "Role",      v: "Founding partner" },
              ].map((row) => (
                <div key={row.k} className="px-5 py-3">
                  <p className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{row.k}</p>
                  <p className="mt-0.5 font-display text-[13.5px] font-black tracking-tight">{row.v}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Chapter>

      {/* ══════════════════════════════════════════════════════════════════════
         CLOSING CTA
         ══════════════════════════════════════════════════════════════════════ */}
      <section className="bg-[#0a0a14] py-24 text-white sm:py-32">
        <div className="mx-auto max-w-4xl px-6 text-center sm:px-10 lg:px-16">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.32em] text-white/40">Epilogue · your clinic</p>
          <h2 className="mt-5 font-display text-3xl font-black leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
            Now it&apos;s open <span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-rose-400 bg-clip-text italic text-transparent">to your clinic</span>.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-[15px] leading-relaxed text-white/70 sm:text-base">
            Eighteen months of forging inside one franchise. Now ready to run yours.
          </p>

          {/* Final reader-mirror punch */}
          <p className="mx-auto mt-8 max-w-2xl font-display text-lg font-semibold italic leading-snug text-white/85 sm:text-xl">
            You built the clinic.{" "}
            <span className="bg-gradient-to-r from-violet-300 to-fuchsia-300 bg-clip-text not-italic text-transparent">
              Let Clinic OS run the operations.
            </span>
          </p>

          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={openDemo}
              data-testid="why-closing-demo"
              className="group inline-flex h-12 items-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-7 text-sm font-bold text-white shadow-lg ring-1 ring-inset ring-white/15 transition-all hover:-translate-y-0.5 hover:shadow-xl"
            >
              <CalendarCheck className="h-4 w-4" />
              Book a demo
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </button>
            <Link
              to="/platform"
              className="inline-flex h-12 items-center gap-2 rounded-xl border border-white/20 bg-white/[0.04] px-7 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-white/[0.08]"
            >
              See the platform
            </Link>
          </div>
        </div>
      </section>

      <DiscChapters
        eyebrow="The story, for your mindset"
        heading="One platform. Four reasons to trust it."
        sub="Whether you're a Driver, Influencer, Steady or Conscientious owner — the same Clinic OS story lands four different ways. Pick yours."
        chapters={WHY_CHAPTERS}
        className="border-t border-border/60 bg-secondary/15"
      />

      <FinalCTA />
    </article>
  );
}
